import { useEffect, useRef } from 'react';
import { AlertTriangle, Check, Download, Scissors, X } from 'lucide-react';

import { Button, Pill } from '../../components/ui';
import type { CutCandidate } from '../../types/api';
import { durationLabel, timecode, verdictTone } from './contract';
import styles from './CutPage.module.css';

interface Props {
  candidate: CutCandidate | null;
  busy?: boolean;
  hint?: string;
  onExtract?: (candidate: CutCandidate) => void;
  onClose: () => void;
}

export function CandidatePreview({ candidate, busy = false, hint, onExtract, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const open = candidate !== null;
  const clip = candidate?.clip?.url ?? null;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !clip) return;
    void video.play().catch(() => undefined);
  }, [clip]);

  return (
    <dialog
      ref={ref}
      className={styles.preview}
      aria-label="Фрагмент"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      {candidate && (
        <div className={styles.previewInner}>
          <header className={styles.previewHead}>
            <span className={styles.previewRange}>
              {timecode(candidate.start_s)} – {timecode(candidate.end_s)}
            </span>
            <span className={styles.previewDur}>{durationLabel(candidate.duration_s)}</span>
            <Pill tone={verdictTone(candidate.score)}>{Math.round(candidate.score * 100)}%</Pill>
            <span className={styles.previewVerdict}>{candidate.verdict}</span>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className={styles.previewClose}
              onClick={onClose}
              aria-label="Закрыть"
            >
              <X size={13} strokeWidth={1.8} />
            </Button>
          </header>

          <div className={styles.previewStage}>
            {clip ? (
              <video
                key={clip}
                ref={videoRef}
                className={styles.previewVideo}
                src={clip}
                controls
                autoPlay
                playsInline
                preload="auto"
              />
            ) : (
              <>
                {candidate.thumbnail && (
                  <img className={styles.previewFrame} src={candidate.thumbnail.url} alt="" />
                )}
                <span className={styles.previewNote}>
                  {busy ? 'Режем, видео появится здесь' : 'Кусок ещё не вырезан'}
                </span>
              </>
            )}
          </div>

          <ul className={styles.previewChecks}>
            {candidate.checks.map((check) => (
              <li
                key={check.key}
                className={`${styles.previewCheck} ${
                  check.ok ? styles.checkOk : styles.checkWarn
                }`}
              >
                {check.ok ? (
                  <Check size={13} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <AlertTriangle size={13} strokeWidth={2} aria-hidden="true" />
                )}
                <span>{check.label}</span>
              </li>
            ))}
          </ul>

          <div className={styles.previewFoot}>
            {clip && (
              <a className={styles.previewLink} href={clip} download>
                <Download size={13} strokeWidth={1.5} aria-hidden="true" />
                Скачать
              </a>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={busy || Boolean(hint)}
              title={hint}
              onClick={() => onExtract?.(candidate)}
            >
              <Scissors size={13} strokeWidth={1.5} aria-hidden="true" />
              {busy ? 'режем…' : clip ? 'Вырезать заново' : 'Вырезать'}
            </Button>
          </div>
        </div>
      )}
    </dialog>
  );
}
